"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDynamicScript = void 0;
/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
const icf_platform_client_1 = require("@atg/icf-platform-client");
const react_1 = require("react");
const FederatedModule_1 = require("./FederatedModule");
const getFederatedModuleFallbackServers_1 = require("./getFederatedModuleFallbackServers");
const loadScriptWithFallbacks_1 = require("./loadScriptWithFallbacks");
function useDynamicScript(scope, url, fallbackMappings) {
    const [status, setStatus] = (0, react_1.useState)(FederatedModule_1.Status.Loading);
    const clientStatsd = (0, icf_platform_client_1.useStatsd)();
    // This has (and always has had) a potential race condition where the component scope could change and then quickly
    // change back to the original scope before the script has loaded, which would result in the script being loaded
    // twice.
    (0, react_1.useEffect)(() => {
        if (window[scope]) {
            setStatus(FederatedModule_1.Status.Ready);
            return;
        }
        const startTime = Date.now();
        // This check retains the current streamate.server behavior of allowing non-configured federated modules to silently
        // "succeed" in tests
        const fallbackServers = url ? (0, getFederatedModuleFallbackServers_1.getFederatedModuleFallbackServers)(fallbackMappings, url) : [];
        setStatus(FederatedModule_1.Status.Loading);
        // In the case where the component has unmounted or the scope has changed, we don't want to set the status or bother
        // continuing with fallback attempts.
        let shouldCancel = false;
        function setStatusWithinScope(newStatus) {
            if (!shouldCancel) {
                setStatus(newStatus);
            }
        }
        (0, loadScriptWithFallbacks_1.loadScriptWithFallbacks)(url, [...fallbackServers], {
            shouldCancel: () => shouldCancel,
            onError: () => {
                void clientStatsd.increment({ key: `federated_module.${scope}.error`, amount: 1 });
            },
            onFallback: () => {
                void clientStatsd.increment({ key: `federated_module.${scope}.fallback`, amount: 1 });
            },
        })
            .then(({ loadedUrl }) => {
            __federated_module_load_urls__[scope] = loadedUrl;
            // Allow successful load statistics to be sent in batch to minimize connections
            const batched = true;
            void clientStatsd.increment({ key: `federated_module.${scope}.onload`, amount: 1 }, undefined, batched);
            setStatusWithinScope(FederatedModule_1.Status.Ready);
        }, () => {
            // All fallbacks have failed
            if (!shouldCancel) {
                void clientStatsd.increment({ key: `federated_module.${scope}.failed`, amount: 1 });
            }
            setStatusWithinScope(FederatedModule_1.Status.Error);
        })
            .finally(() => {
            const duration = Date.now() - startTime;
            void clientStatsd.timing({ key: `federated_module.${scope}`, time: duration }, undefined, true);
        });
        return () => {
            shouldCancel = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope, url]);
    return status;
}
exports.useDynamicScript = useDynamicScript;
